// Crafting recipe matching (ARCH §3.3, UI-4). PURE (ENG-1).
//
// Matches a crafting grid (2×2 inventory or 3×3 table) against the recipe
// registry. Shaped recipes match position-for-position after both the grid and
// the pattern are trimmed to their bounding box, and also match their mirror
// (Minecraft folds horizontal reflections). Shapeless recipes match as a multiset.
// Ingredients are item names or `#tag`s resolved against the item registry.
import type { Registries } from '../registry/load';
import type { RecipeDef } from '../registry/schemas';
import { type ItemStack, isEmpty, makeStack } from './inventory';

/** Does `ingredient` (item name or `#tag`) match the item in `stack`? */
export function ingredientMatches(reg: Registries, ingredient: string, stack: ItemStack): boolean {
  if (isEmpty(stack)) return false;
  const name = reg.items.nameOf[stack.item];
  if (name === undefined) return false;
  if (ingredient.startsWith('#')) {
    return reg.items.byName.get(name)?.tags.includes(ingredient.slice(1)) ?? false;
  }
  return name === ingredient;
}

function makeResult(reg: Registries, recipe: RecipeDef): ItemStack | null {
  const id = reg.items.idOf.get(recipe.result.item);
  if (id === undefined) return null;
  return makeStack(id, recipe.result.count);
}

/** Grid as a 2D item-id array trimmed to its non-empty bounding box. */
interface Trimmed {
  cells: ItemStack[]; // row-major, w*h
  w: number;
  h: number;
}

function trimGrid(grid: readonly ItemStack[], width: number): Trimmed | null {
  const height = Math.floor(grid.length / width);
  let r0 = height, r1 = -1, c0 = width, c1 = -1;
  for (let r = 0; r < height; r++) {
    for (let c = 0; c < width; c++) {
      if (!isEmpty(grid[r * width + c]!)) {
        r0 = Math.min(r0, r); r1 = Math.max(r1, r);
        c0 = Math.min(c0, c); c1 = Math.max(c1, c);
      }
    }
  }
  if (r1 < 0) return null; // empty grid
  const w = c1 - c0 + 1, h = r1 - r0 + 1;
  const cells: ItemStack[] = [];
  for (let r = r0; r <= r1; r++) for (let c = c0; c <= c1; c++) cells.push(grid[r * width + c]!);
  return { cells, w, h };
}

/** Trim a recipe pattern (rows of chars, ' ' = empty) to its bounding box. */
export function trimPattern(pattern: readonly string[]): { chars: string[]; w: number; h: number } {
  const height = pattern.length;
  const width = Math.max(...pattern.map((r) => r.length));
  const at = (r: number, c: number): string => pattern[r]![c] ?? ' ';
  let r0 = height, r1 = -1, c0 = width, c1 = -1;
  for (let r = 0; r < height; r++) {
    for (let c = 0; c < width; c++) {
      if (at(r, c) !== ' ') {
        r0 = Math.min(r0, r); r1 = Math.max(r1, r);
        c0 = Math.min(c0, c); c1 = Math.max(c1, c);
      }
    }
  }
  if (r1 < 0) return { chars: [], w: 0, h: 0 };
  const w = c1 - c0 + 1, h = r1 - r0 + 1;
  const chars: string[] = [];
  for (let r = r0; r <= r1; r++) for (let c = c0; c <= c1; c++) chars.push(at(r, c));
  return { chars, w, h };
}

function matchShapedOrientation(
  reg: Registries, key: Record<string, string>, pat: { chars: string[]; w: number; h: number },
  grid: Trimmed, mirror: boolean,
): boolean {
  if (pat.w !== grid.w || pat.h !== grid.h) return false;
  for (let r = 0; r < grid.h; r++) {
    for (let c = 0; c < grid.w; c++) {
      const pc = mirror ? grid.w - 1 - c : c;
      const ch = pat.chars[r * grid.w + pc]!;
      const cell = grid.cells[r * grid.w + c]!;
      if (ch === ' ') {
        if (!isEmpty(cell)) return false;
      } else {
        const ing = key[ch];
        if (ing === undefined || !ingredientMatches(reg, ing, cell)) return false;
      }
    }
  }
  return true;
}

function matchShapeless(reg: Registries, ingredients: readonly string[], grid: Trimmed): boolean {
  const items = grid.cells.filter((s) => !isEmpty(s));
  if (items.length !== ingredients.length) return false;
  const used = new Array(items.length).fill(false);
  // Backtracking assignment (≤9 items) so overlapping tag/name ingredients resolve.
  const assign = (k: number): boolean => {
    if (k === ingredients.length) return true;
    for (let i = 0; i < items.length; i++) {
      if (used[i] || !ingredientMatches(reg, ingredients[k]!, items[i]!)) continue;
      used[i] = true;
      if (assign(k + 1)) return true;
      used[i] = false;
    }
    return false;
  };
  return assign(0);
}

export interface CraftMatch {
  result: ItemStack;
  recipe: RecipeDef;
}

/**
 * Find the crafting result for `grid` (row-major, `width` columns). Returns the
 * output stack + matched recipe, or null if nothing matches. Only crafting-table
 * recipes (shaped/shapeless) are considered; a 2×2 grid can still make anything
 * that fits in 2×2.
 */
export function matchCrafting(reg: Registries, grid: readonly ItemStack[], width: number): CraftMatch | null {
  const trimmed = trimGrid(grid, width);
  if (!trimmed) return null;
  for (const recipe of reg.recipes.list) {
    if (recipe.type === 'shaped') {
      const pat = trimPattern(recipe.pattern);
      if (
        matchShapedOrientation(reg, recipe.key, pat, trimmed, false) ||
        matchShapedOrientation(reg, recipe.key, pat, trimmed, true)
      ) {
        const result = makeResult(reg, recipe);
        if (result) return { result, recipe };
      }
    } else if (recipe.type === 'shapeless') {
      if (matchShapeless(reg, recipe.ingredients, trimmed)) {
        const result = makeResult(reg, recipe);
        if (result) return { result, recipe };
      }
    }
  }
  return null;
}

/** Smelting/blasting/smoking result for a single input item (M6 executes it). */
export function matchCooking(
  reg: Registries, input: ItemStack, kinds: readonly RecipeDef['type'][] = ['smelting'],
): CraftMatch | null {
  if (isEmpty(input)) return null;
  for (const recipe of reg.recipes.list) {
    if (!kinds.includes(recipe.type)) continue;
    if (recipe.type === 'smelting' || recipe.type === 'blasting' || recipe.type === 'smoking' || recipe.type === 'campfire') {
      if (ingredientMatches(reg, recipe.ingredient, input)) {
        const result = makeResult(reg, recipe);
        if (result) return { result, recipe };
      }
    }
  }
  return null;
}
